import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useParams, useNavigate } from "@remix-run/react";
import { motion, AnimatePresence } from "framer-motion";
import { FaRedo, FaCheck, FaArrowLeft } from 'react-icons/fa';
import { HiOutlineShare, HiOutlineClipboardCopy } from 'react-icons/hi';
import { json, type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { getAdhkarSEOConfigBySlug, generateStructuredData } from "~/shared/utils/adhkar-seo";
import { adhkarList } from "~/features/adhkar/data/adhkar-data";

// Styles
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/shared/components/performance.css";
import "~/features/adhkar/adhkar.css";
import "~/features/adhkar/seo-enhancements.css";

// Types
type Lang = 'ar' | 'en';

// UI Translations
const translations: Record<Lang, Record<string, string>> = {
  ar: {
    title: "أذكاري",
    subtitle: "استعرض أذكارك اليومية بسهولة وجمال",
    switchLang: "English",
    back: "العودة للصفحة الرئيسية",
    backToSections: "العودة للأقسام",
    repeat: "فائدة",
    selectSection: "اختر قسم الأذكار",
    remaining: "متبقي",
    completed: "مكتمل",
    reset: "إعادة تعيين",
    copy: "نسخ الذكر",
    share: "مشاركة الذكر",
    copied: "تم النسخ!",
    shared: "تم المشاركة!",
    notFound: "القسم غير موجود",
    goBack: "العودة للأقسام"
  },
  en: {
    title: "Adhkar",
    subtitle: "Browse your daily Azkar easily and beautifully",
    switchLang: "العربية",
    back: "Back to Home",
    backToSections: "Back to Sections",
    repeat: "Repeat",
    selectSection: "Select Adhkar Section",
    remaining: "Remaining",
    completed: "Completed",
    reset: "Reset",
    copy: "Copy Dhikr",
    share: "Share Dhikr",
    copied: "Copied!",
    shared: "Shared!",
    notFound: "Section not found",
    goBack: "Back to Sections"
  }
};

// Loader function for SEO
export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params;
  
  if (!slug) {
    throw new Response("Not Found", { status: 404 });
  }

  const seoConfig = getAdhkarSEOConfigBySlug(slug);
  
  if (!seoConfig) {
    throw new Response("Not Found", { status: 404 });
  }

  return json({ seoConfig });
}

// Meta function for SEO
export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  if (!data?.seoConfig) {
    return [
      { title: "أذكاري - القسم غير موجود | Adhkar - Section Not Found" },
      { name: "description", content: "القسم المطلوب غير موجود في أذكاري" }
    ];
  }

  const { seoConfig } = data;
  const lang = 'ar'; // Default to Arabic, can be made dynamic

  return [
    { title: seoConfig.metaTitle[lang] },
    { name: "description", content: seoConfig.metaDescription[lang] },
    { name: "keywords", content: seoConfig.keywords[lang].join(', ') },
    { property: "og:title", content: seoConfig.metaTitle[lang] },
    { property: "og:description", content: seoConfig.metaDescription[lang] },
    { property: "og:type", content: "website" },
    { property: "og:url", content: `https://yourdomain.com/adhkar/${params.slug}` },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: seoConfig.metaTitle[lang] },
    { name: "twitter:description", content: seoConfig.metaDescription[lang] },
    { rel: "canonical", href: `https://yourdomain.com/adhkar/${params.slug}` },
    {
      "script:ld+json": JSON.stringify(generateStructuredData(seoConfig, lang))
    }
  ];
};

export default function AdhkarSlugPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lang, setLang] = useState<Lang>('ar');
  const [counters, setCounters] = useState<Record<string, number>>({});
  const [notification, setNotification] = useState<{ message: string; type: 'copy' | 'share' } | null>(null);
  const t = translations[lang];

  // Find the section based on slug
  const seoConfig = getAdhkarSEOConfigBySlug(slug || '');
  const section = useMemo(() => {
    if (!seoConfig) return null;
    return adhkarList.find(s => s.id === seoConfig.id);
  }, [seoConfig]);

  // Redirect if section not found
  useEffect(() => {
    if (!section) {
      navigate('/adhkar', { replace: true });
    }
  }, [section, navigate]);

  if (!section || !seoConfig) {
    return (
      <div className="adhkar-app" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-pink-800 to-purple-600">
          <div className="text-center text-white">
            <h1 className="text-2xl font-bold mb-4">{t.notFound}</h1>
            <Link
              to="/adhkar"
              className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              {t.goBack}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleDhikrClick = useCallback((itemIndex: number) => {
    const item = section.items[itemIndex];
    const counterKey = `${section.id}-${itemIndex}`;
    const currentCount = counters[counterKey] !== undefined ? counters[counterKey] : item.count;
    
    if (currentCount > 0) {
      setCounters(prev => ({
        ...prev,
        [counterKey]: currentCount - 1
      }));
    }
  }, [section, counters]);

  const resetCounter = useCallback((itemIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const counterKey = `${section.id}-${itemIndex}`;
    const item = section.items[itemIndex];
    
    setCounters(prev => ({
      ...prev,
      [counterKey]: item.count
    }));
  }, [section]);

  const copyDhikr = useCallback(async (itemIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const item = section.items[itemIndex];
    const textToCopy = `${item.text[lang]}\n\n${t.repeat}: ${item.repeat[lang]}`;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setNotification({ message: t.copied, type: 'copy' });
      setTimeout(() => setNotification(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, [section, lang, t]);

  const shareDhikr = useCallback(async (itemIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const item = section.items[itemIndex];
    
    // بناء النص للمشاركة مع جميع التفاصيل
    let textToShare = '';
    
    // إضافة البسملة للآيات القرآنية
    if (item.type === 'quran' && item.basmala) {
      textToShare += `${item.basmala[lang]}\n\n`;
    }
    
    // إضافة الاستعاذة للآيات القرآنية
    if (item.type === 'quran' && item.istiatha) {
      textToShare += `${item.istiatha[lang]}\n\n`;
    }
    
    // النص الرئيسي
    textToShare += `${item.text[lang]}\n\n`;
    
    // عدد التكرار
    textToShare += `${t.repeat}: ${item.repeat[lang]}\n`;
    
    // المرجع إن وجد
    if (item.reference) {
      textToShare += `${lang === 'ar' ? 'المرجع' : 'Reference'}: ${item.reference[lang]}\n`;
    }
    
    // اسم القسم والتطبيق
    textToShare += `\n📿 ${section.category[lang]} - ${t.title}`;
    
    try {
      if (navigator.share && navigator.canShare && navigator.canShare({ text: textToShare })) {
        await navigator.share({
          title: `${item.reference ? item.reference[lang] + ' - ' : ''}${section.category[lang]}`,
          text: textToShare,
          url: window.location.href
        });
        setNotification({ message: t.shared, type: 'share' });
        setTimeout(() => setNotification(null), 3000);
      } else {
        // Fallback to copy if sharing is not supported
        await navigator.clipboard.writeText(textToShare);
        setNotification({ message: t.copied, type: 'copy' });
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (err) {
      console.error('Failed to share: ', err);
      // Fallback to copy on error
      try {
        await navigator.clipboard.writeText(textToShare);
        setNotification({ message: t.copied, type: 'copy' });
        setTimeout(() => setNotification(null), 3000);
      } catch (copyErr) {
        console.error('Failed to copy as fallback: ', copyErr);
        setNotification({ 
          message: lang === 'ar' ? 'حدث خطأ في المشاركة' : 'Share failed', 
          type: 'copy' 
        });
        setTimeout(() => setNotification(null), 3000);
      }
    }
  }, [section, lang, t]);

  const getCounterDisplay = (itemIndex: number) => {
    const item = section.items[itemIndex];
    const counterKey = `${section.id}-${itemIndex}`;
    const currentCount = counters[counterKey] !== undefined ? counters[counterKey] : item.count;
    
    return {
      current: currentCount,
      total: item.count,
      isCompleted: currentCount === 0
    };
  };

  return (
    <div className="adhkar-app" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="adhkar-header">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/adhkar"
              className="adhkar-btn"
              aria-label={lang === 'ar' ? "العودة إلى أذكاري" : "Back to Adhkar"}
            >
              <FaArrowLeft className="w-4 h-4" />
            </Link>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
                className="lang-switcher"
                aria-label={lang === 'ar' ? "Switch to English" : "التبديل إلى العربية"}
              >
                {lang === 'ar' ? 'EN' : 'عربي'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative flex flex-col items-center justify-center min-h-[40vh] w-full bg-gradient-to-br from-purple-900 via-pink-800 to-purple-600 overflow-hidden">
        <div className="relative z-10 text-center px-4 pt-16 pb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-extrabold text-3xl sm:text-4xl md:text-5xl text-white drop-shadow-2xl leading-tight mb-4"
          >
            {seoConfig.title[lang]}
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-center text-purple-100 text-lg md:text-xl mt-4 font-medium drop-shadow-lg max-w-3xl mx-auto"
          >
            {seoConfig.description[lang]}
          </motion.p>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative bg-gradient-to-b from-white/90 via-purple-50/80 to-white dark:from-gray-800 dark:via-purple-900/80 dark:to-gray-800">
        <main className="max-w-4xl mx-auto px-4 py-6 md:py-8 -mt-4 md:-mt-8 relative z-10">
          {/* Breadcrumb Navigation */}
          <nav className="adhkar-breadcrumb mb-6" aria-label="Breadcrumb">
            <Link to="/" className="adhkar-seo-link">
              {lang === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <span className="separator">/</span>
            <Link to="/adhkar" className="adhkar-seo-link">
              {lang === 'ar' ? 'أذكاري' : 'Adhkar'}
            </Link>
            <span className="separator">/</span>
            <span className="text-gray-600 dark:text-gray-400">
              {seoConfig.title[lang]}
            </span>
          </nav>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${section.color} flex items-center justify-center text-2xl`}>
                  {section.icon}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-purple-700 dark:text-purple-300">
                    {section.category[lang]}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {section.description[lang]}
                  </p>
                </div>
              </div>
            </div>
            
            <ul className="space-y-4 md:space-y-6">
              {section.items.map((item, i) => {
                const counter = getCounterDisplay(i);
                return (
                  <motion.div 
                    key={`${section.id}-${i}`} 
                    onClick={() => !counter?.isCompleted && handleDhikrClick(i)}
                    className={`adhkar-card rounded-lg md:rounded-xl shadow-lg transition-all duration-300 overflow-hidden cursor-pointer ${
                      counter?.isCompleted 
                        ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/10 border-2 border-green-200 dark:border-green-700' 
                        : 'bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/10 border-2 border-transparent hover:border-purple-200 dark:hover:border-purple-700'
                    }`}
                    whileHover={!counter?.isCompleted ? { scale: 1.01, y: -1 } : {}}
                    whileTap={!counter?.isCompleted ? { scale: 0.98 } : {}}
                  >
                    <div className="p-4 md:p-6">
                      <p className="adhkar-item-text text-base md:text-lg font-semibold text-gray-800 dark:text-white leading-relaxed mb-2">
                        {item.text[lang]}
                      </p>
                      <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                        {t.repeat}: {item.repeat[lang]}
                      </span>
                    </div>
                    
                    <div className={`adhkar-counter w-full h-14 sm:h-16 md:h-20 flex items-center justify-center transition-all duration-300 relative ${
                      counter?.isCompleted 
                        ? 'bg-green-500/10 dark:bg-green-500/20' 
                        : 'bg-purple-500/10 dark:bg-purple-500/20'
                    }`}>
                      <div 
                        className={`font-bold text-xl sm:text-2xl md:text-3xl transition-all duration-300 ${
                          counter?.isCompleted 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-purple-600 dark:text-purple-400'
                        }`}
                      >
                        {counter?.isCompleted ? (
                          <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                            <FaCheck className="text-lg sm:text-xl md:text-2xl" />
                            <span className="text-sm sm:text-base md:text-lg font-medium">
                              {lang === 'ar' ? 'مكتمل' : 'Done'}
                            </span>
                          </div>
                        ) : (
                          <div className="text-xl sm:text-2xl md:text-3xl font-black">
                            {counter?.current}
                          </div>
                        )}
                      </div>
                      
                      <div className="absolute right-1.5 sm:right-2 md:right-3 flex items-center gap-1 sm:gap-1.5">
                        <button
                          onClick={(e) => copyDhikr(i, e)}
                          className={`p-1.5 sm:p-2 rounded-lg transition-colors duration-200 ${
                            counter?.isCompleted 
                              ? 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30' 
                              : 'bg-white text-purple-600 hover:bg-purple-50 dark:bg-gray-800 dark:text-purple-400 dark:hover:bg-purple-900/20'
                          } shadow-sm border border-gray-200/50 dark:border-gray-700/50`}
                          title={t.copy}
                        >
                          <HiOutlineClipboardCopy className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </button>
                        
                        <button
                          onClick={(e) => shareDhikr(i, e)}
                          className={`p-1.5 sm:p-2 rounded-lg transition-colors duration-200 ${
                            counter?.isCompleted 
                              ? 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30' 
                              : 'bg-white text-blue-600 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-blue-900/20'
                          } shadow-sm border border-gray-200/50 dark:border-gray-700/50`}
                          title={t.share}
                        >
                          <HiOutlineShare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </button>
                        
                        <button
                          onClick={(e) => resetCounter(i, e)}
                          className={`p-1.5 sm:p-2 rounded-lg transition-colors duration-200 ${
                            counter?.isCompleted 
                              ? 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30' 
                              : 'bg-white text-orange-600 hover:bg-orange-50 dark:bg-gray-800 dark:text-orange-400 dark:hover:bg-orange-900/20'
                          } shadow-sm border border-gray-200/50 dark:border-gray-700/50`}
                          title={t.reset}
                        >
                          <FaRedo className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </ul>
          </motion.div>
        </main>
      </div>

      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg border ${
              notification.type === 'copy' 
                ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700'
                : 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700'
            }`}
          >
            <div className="flex items-center gap-2">
              {notification.type === 'copy' ? (
                <HiOutlineClipboardCopy className="w-4 h-4" />
              ) : (
                <HiOutlineShare className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
